import {
  Component,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
  output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { lastValueFrom } from 'rxjs';
import { CustomMomentDateAdapter } from 'src/app/shared/custom.moment.date.adapter';
import { EmployeeSearchComponent } from 'src/app/shared/components/employee-search/employee-search.component';
import { MonthFieldComponent } from 'src/app/shared/components/month-field/month-field.component';
import { MaterialModule } from 'src/app/shared/material.module';
import { EmployeeProject, IEmployee } from 'src/app/shared/models';
import {
  AdminEmployeeService,
  AlertService,
  AlertType,
  AuthService,
  EmployeeService,
  ReportService,
} from 'src/app/shared/services';
import { DATE_FORMATS } from 'src/app/shared/services/helpers/date.helper';
import { SharedModule } from 'src/app/shared/shared.module';

interface SubmitValue {
  fromDate: string;
  employeeId?: number;
  project?: EmployeeProject;
  toDate: string;
}

@Component({
  imports: [
    EmployeeSearchComponent,
    MaterialModule,
    MonthFieldComponent,
    ReactiveFormsModule,
    SharedModule,
    TranslateModule,
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomMomentDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
  ],
  selector: 'app-emp-report-export',
  styleUrls: ['./employee-report-export.component.scss'],
  templateUrl: './employee-report-export.component.html',
})
export class EmployeeReportExportComponent implements OnInit {
  projects = input<EmployeeProject[]>([]);
  private adminEmployeeService = inject(AdminEmployeeService);
  private alertService = inject(AlertService);
  private employeeService = inject(EmployeeService);
  private reportService = inject(ReportService);
  authService = inject(AuthService);
  translate = inject(TranslateService);

  isLoading = false;
  private firstDateOfTheMonth = moment().add(-1, 'month').startOf('month');

  employeeList: IEmployee[] = [];
  minDate = moment().add(-24, 'months').startOf('month');
  maxDate = moment();
  maxDateStartOfMonth = moment().startOf('month');
  projectList = signal<EmployeeProject[]>([]);

  // controls
  dateControl = new FormControl(moment());
  employeeControl: FormControl = new FormControl();
  fromDateControl = new FormControl(moment().add(-1, 'month').startOf('month'));
  toDateControl = new FormControl(moment().add(-1, 'month').endOf('month'));
  monthControl = new FormControl(this.firstDateOfTheMonth);
  projectControl: FormControl = new FormControl();

  readonly maxDateRange = moment();
  readonly minDateRange = moment().add(-24, 'months').startOf('month');

  get toDateMax(): moment.Moment {
    const threeMonthsAfterFrom = this.fromDateControl.value?.clone().add(3, 'months');
    return threeMonthsAfterFrom && threeMonthsAfterFrom.isBefore(this.maxDateRange)
      ? threeMonthsAfterFrom
      : this.maxDateRange;
  }

  get isRangeCapped(): boolean {
    const from = this.fromDateControl.value;
    const to = this.toDateControl.value;
    if (!from || !to) return false;
    return to.isSameOrAfter(from.clone().add(3, 'months'), 'day');
  }

  readonly enabledControls = input({
    date: false,
    dateRange: false,
    dateRangeToggle: false,
    employee: false,
    month: false,
    project: false,
  });

  rangeMode = signal(false);

  onRangeModeChange(useRange: boolean) {
    this.rangeMode.set(useRange);
  }
  readonly title = input<string>(undefined);
  readonly exportErrorMsg = input('');
  readonly employeeEndpointPath = input<string>(undefined);

  @Output() readonly emitSubmit = new EventEmitter<SubmitValue>();
  readonly employeeChange = output<SubmitValue>();

  get isDateControlValid(): boolean {
    return !this.enabledControls().date || this.dateControl.valid;
  }

  get isMonthControlValid(): boolean {
    return !this.useMonth || this.monthControl.valid;
  }

  get isProjectControlValid(): boolean {
    return !this.enabledControls().project || this.projectControl.valid;
  }

  get useDateRange(): boolean {
    const enabledControls = this.enabledControls();
    if (enabledControls.dateRangeToggle) {
      return this.rangeMode();
    }
    return enabledControls.dateRange;
  }

  get useMonth(): boolean {
    const enabledControls = this.enabledControls();
    if (enabledControls.dateRangeToggle) {
      return !this.rangeMode();
    }
    return enabledControls.month;
  }

  get isSubmitDisabled(): boolean {
    const enabledControls = this.enabledControls();
    return (
      (enabledControls.date && !this.dateControl.valid) ||
      (this.useDateRange && (!this.fromDateControl.valid || !this.toDateControl.valid)) ||
      (this.useMonth && !this.monthControl.valid) ||
      (enabledControls.project && !this.projectControl.value)
    );
  }

  displayProjectNameFn(project: EmployeeProject): string {
    return project ? `${project.name}` : null;
  }

  async initProjects() {
    let projects: EmployeeProject[];
    if (this.projects()?.length) {
      projects = this.projects();
    } else {
      projects = await this.employeeService.loadMyProjects(this.authService.userId());
    }

    projects.sort((a, b) => (a.name.toLocaleUpperCase() > b.name.toLocaleUpperCase() ? 1 : -1));
    this.projectList.set(projects);

    if (this.projectList().length === 1) {
      this.projectControl.setValue(this.projectList()[0]);
    }
  }

  ngOnInit() {
    this.initProjects();
  }

  onEmployeeSelect(employeeId: number) {
    this.employeeControl.setValue(employeeId);
    if (employeeId) this.employeeChange.emit(this.generateReturnObject());
  }

  async submit() {
    if (!this.validateInput()) {
      return;
    }

    // Emit the submit event with the form data if emitSubmit output has been passed
    if (this.emitSubmit.observed) {
      this.emitSubmit.emit(this.generateReturnObject());
      return;
    }

    this.isLoading = true;
    const { fromDate } = this.generateReturnObject();
    try {
      const { buffer, fileName } = await lastValueFrom(
        this.adminEmployeeService.fetchExcelReport(`${this.employeeEndpointPath()}${fromDate}`),
      );

      this.reportService.saveAsExcelFile({ buffer, fileName });
    } catch (error) {
      this.alertService.showAlert(this.exportErrorMsg(), AlertType.error);
    } finally {
      this.isLoading = false;
    }
  }

  private generateReturnObject(): SubmitValue {
    const fromDate = this.useDateRange
      ? this.fromDateControl.value.format('YYYY-MM-DD')
      : this.enabledControls().date
        ? this.dateControl.value.format('YYYY-MM-DD')
        : this.monthControl.value.startOf('month').format('YYYY-MM-DD');
    const toDate = this.useDateRange
      ? this.toDateControl.value.format('YYYY-MM-DD')
      : this.monthControl.value.endOf('month').format('YYYY-MM-DD');
    return {
      fromDate,
      employeeId: this.employeeControl.value,
      project: this.projectControl.value,
      toDate,
    };
  }

  private validateInput(): boolean {
    if (!this.isDateControlValid || !this.isMonthControlValid || !this.isProjectControlValid) {
      this.alertService.showAlert(
        this.translate.instant('ADMIN.CLIENT_HOURS.FILL_DATE_AND_PROJECT').toString(),
        AlertType.error,
      );
      return false;
    }
    return true;
  }
}
